import { useEffect, useState, type FC, type ReactNode } from "react";
import { Column } from "primereact/column";
import { DataTable, type DataTablePageEvent } from "primereact/datatable";
import { Dropdown, type DropdownChangeEvent } from "primereact/dropdown";
import DataTableCard from "@/components/DataTableCard/DataTableCard";
import styles from "@/components/DataTableCard/DataTableCard.module.css";
import { TEXT } from "@/constants/text";
import type { ArrivalReportRow } from "@/types/reporting";
import {
  CSV_ESCAPE,
  RPP_OPTIONS,
  fmt,
  fmtDwell,
  fmtStr,
  integerFormatter,
} from "@/utils/tableFormatters";

const POI_NAME_BODY = ({ poi_name: poiName }: ArrivalReportRow): ReactNode => (
  <span title={poiName}>{fmtStr(poiName)}</span>
);

const POI_ADDRESS_BODY = ({
  poi_address: poiAddress,
}: ArrivalReportRow): ReactNode => (
  <span title={poiAddress}>{fmtStr(poiAddress)}</span>
);

interface ArrivalPoiTableProps {
  error: string | null;
  isLoading: boolean;
  rows: ArrivalReportRow[];
}

const TABLE_TEXT = TEXT.campaignOverview.arrival;

const FILTER_FIELDS = ["poi_address", "poi_name"] as const;

// Exclude "All" option to prevent mounting 10K+ rows at once.
const RPP_OPTIONS_PAGINATED = RPP_OPTIONS.filter((opt) => opt.value > 0);

const ArrivalPoiTable: FC<ArrivalPoiTableProps> = ({
  error,
  isLoading,
  rows,
}) => {
  const [first, setFirst] = useState(0);
  const [pageRows, setPageRows] = useState(25);
  const [globalFilter, setGlobalFilter] = useState("");

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setGlobalFilter(e.target.value);
    setFirst(0);
  };

  const handlePage = (e: DataTablePageEvent) => {
    setFirst(e.first);
  };

  const handleRowsChange = (e: DropdownChangeEvent) => {
    setPageRows(e.value as number);
    setFirst(0);
  };

  useEffect(() => {
    setFirst(0);
  }, [rows]);

  const handleExportCsv = () => {
    const term = globalFilter.trim().toLowerCase();
    const exportRows = term
      ? rows.filter((row) =>
          FILTER_FIELDS.some((field) => {
            const val = row[field];
            return typeof val === "string" && val.toLowerCase().includes(term);
          })
        )
      : rows;

    const headers = [
      TABLE_TEXT.poiColName,
      TABLE_TEXT.poiColAddress,
      TABLE_TEXT.poiColVisits,
      TABLE_TEXT.poiColUniqueVisits,
      TABLE_TEXT.poiColDwell,
    ].join(",");

    const body = exportRows
      .map((row) =>
        [
          CSV_ESCAPE(fmtStr(row.poi_name)),
          CSV_ESCAPE(fmtStr(row.poi_address)),
          CSV_ESCAPE(fmt(row.projected_visits, integerFormatter)),
          CSV_ESCAPE(fmt(row.projected_unique_visits, integerFormatter)),
          CSV_ESCAPE(fmtDwell(row.avg_dwell_time_min)),
        ].join(",")
      )
      .join("\n");

    const blob = new Blob([`${headers}\n${body}`], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "arrival-poi.csv";
    anchor.click();
    // Defer revoke so the browser has time to initiate the async blob fetch.
    setTimeout(() => URL.revokeObjectURL(url), 100);
  };

  return (
    <DataTableCard
      csvButtonDisabled={isLoading || Boolean(error) || rows.length === 0}
      csvButtonLabel={TABLE_TEXT.poiCsvButton}
      error={error ? TABLE_TEXT.poiError : null}
      filterPlaceholder={TABLE_TEXT.poiFilterPlaceholder}
      globalFilter={globalFilter}
      isLoading={isLoading}
      title={TABLE_TEXT.poiTitle}
      onExportCsv={handleExportCsv}
      onFilterChange={handleFilterChange}
    >
      <DataTable
        className={styles.table}
        columnResizeMode="fit"
        currentPageReportTemplate="{first}–{last} of {totalRecords}"
        emptyMessage={
          globalFilter.trim()
            ? TABLE_TEXT.poiEmptyMessage
            : TABLE_TEXT.poiNoData
        }
        first={first}
        globalFilter={globalFilter}
        globalFilterFields={[...FILTER_FIELDS]}
        paginator
        paginatorLeft={
          <div className={styles.rppGroup}>
            <label htmlFor="arrival-poi-rpp">{TABLE_TEXT.poiRowsPerPage}</label>
            <Dropdown
              className={styles.rppDropdown}
              inputId="arrival-poi-rpp"
              options={RPP_OPTIONS_PAGINATED}
              pt={{ panel: { className: styles.rppDropdownPanel } }}
              value={pageRows}
              onChange={handleRowsChange}
            />
          </div>
        }
        paginatorTemplate="CurrentPageReport PrevPageLink PageLinks NextPageLink"
        pt={{
          bodyRow: { className: styles.tableRow },
          header: { className: styles.columnHeader },
        }}
        removableSort
        resizableColumns
        rows={pageRows}
        scrollable
        scrollHeight="28rem"
        sortField="projected_visits"
        sortOrder={-1}
        tableStyle={{ tableLayout: "fixed" }}
        value={rows}
        onPage={handlePage}
      >
        <Column
          body={POI_NAME_BODY}
          field="poi_name"
          header={TABLE_TEXT.poiColName}
          sortable
          style={{ width: "25%" }}
        />
        <Column
          body={POI_ADDRESS_BODY}
          field="poi_address"
          header={TABLE_TEXT.poiColAddress}
          sortable
          style={{ width: "35%" }}
        />
        <Column
          body={(row: ArrivalReportRow) =>
            fmt(row.projected_visits, integerFormatter)
          }
          field="projected_visits"
          header={TABLE_TEXT.poiColVisits}
          headerStyle={{ textAlign: "right" }}
          sortable
          style={{ textAlign: "right", width: "15%" }}
        />
        <Column
          body={(row: ArrivalReportRow) =>
            fmt(row.projected_unique_visits, integerFormatter)
          }
          field="projected_unique_visits"
          header={TABLE_TEXT.poiColUniqueVisits}
          headerStyle={{ textAlign: "right" }}
          sortable
          style={{ textAlign: "right", width: "15%" }}
        />
        <Column
          body={(row: ArrivalReportRow) => fmtDwell(row.avg_dwell_time_min)}
          field="avg_dwell_time_min"
          header={TABLE_TEXT.poiColDwell}
          headerStyle={{ textAlign: "right" }}
          sortable
          style={{ textAlign: "right", width: "10%" }}
        />
      </DataTable>
    </DataTableCard>
  );
};

export default ArrivalPoiTable;
